/**
 * @param {number} ms
 */
/**
 * Setup dropzone instance for file fields
 * @param {HTMLFieldSetElement} field
 * @param {Function|null} persistCurrentForm
 */
function setupDropzone(field, persistCurrentForm = null) {
	if (!field) {
		console.error('Please provide an field to dropzone');
		return;
	}

	window.dropzones = window.dropzones || [];

	const formElement = field.closest('form');
	const formId = formElement.id;
	const submitElement = formElement.querySelector('[type="submit"]');

	const hiddenInputElement = field.querySelector('input.input--hiddenfield');
	const dropzoneAreaElement = field.querySelector('.dropzone-area');
	const dropzonePreviewTemplateElement = field.querySelector('.dropzone-preview-template');
	const dropzoneFilesPreviewsElement = field.querySelector('.dropzone-files-previews');
	const dropzoneUploadButton = field.querySelector('.dropzone-upload-button');
	const uploadEndpoint = '/wp-json/contract-form/attachment-handler';
	const getContractID = () => formElement.querySelector('[name="contract-id"]').value


	const getHiddenInputData = () => {
		let parsedValue;
		try {
			parsedValue = JSON.parse(hiddenInputElement.value);
		} catch (error) {
			parsedValue = [];
		}

		return Array.isArray(parsedValue) ? parsedValue : [];
	}


	/**
	 * @typedef {{
	 *     action: string;
	 *     payload: Record<string, any>
	 * }} HiddenInputAction
	 * @param {HiddenInputAction} hiddenInputAction
	 */
	const updateHiddenInput = (hiddenInputAction) => {
		const { action = '', payload = {} } = hiddenInputAction;

		if (!hiddenInputElement) {
			console.error('There is no hidden input')
			return;
		}

		if (!payload?.id) {
			console.error(`Payload doesn't have an id property`);
			return;
		}

		let parsedValue = getHiddenInputData();

		if (action === 'add') {
			parsedValue = [...parsedValue, payload];
		} else if (action === 'remove') {
			parsedValue = parsedValue.filter((detail) => detail?.id !== payload.id);
		}

		hiddenInputElement.value = parsedValue.length > 0 ? JSON.stringify(parsedValue) : '';
		if (persistCurrentForm && typeof persistCurrentForm === 'function') {
			persistCurrentForm()
		}
	};

	// Add some additional attributes to preview element
	const wrapPreviewElement = (previewElement, data) => {
		previewElement.dataset.id = data.id;
		previewElement.title = data.title;
	}

	// When file is added to the dropzone - hold the upload, get recaptcha token and then process
	let recaptcha_token = '';

	const baseConfig = {
		url: uploadEndpoint,
		autoQueue: false,
		autoProcessQueue: false,
		uploadMultiple: true,
		previewTemplate: dropzonePreviewTemplateElement.innerHTML,
		previewsContainer: dropzoneFilesPreviewsElement,
		hiddenInputContainer: field,
		clickable: dropzoneUploadButton,
		/**
		 * The maximum filesize (in MiB) that is allowed to be uploaded.
		 */
		maxFilesize: 10,
		parallelUploads: 1,
		/**
		 * Max amount of files for a ticket
		 */
		maxFiles: 3,
		acceptedFiles: 'image/jpeg,image/png,application/pdf',
		/**
		 * The name of the file param that gets transferred.
		 *
		 * **NOTE**: If you have the option  `uploadMultiple` set to `true`, then Dropzone will append `[]` to the name.
		 */
		paramName: 'upload_files',
		createImageThumbnails: false,
	}
	const events = {
		addedfile: async function (file) {
			if (file?.persisted) {
				wrapPreviewElement(file.previewElement, { id: file.id, title: file.name });
				file.previewElement.classList.add('dz-success');
			}

		},
		uploadprogress: function (file, progress) {
			const progressElement = file.previewElement.querySelector('.upload-progress');
			if (progressElement) {
				progressElement.style.setProperty('--progress', progress);
			}
		},
		removedfile: async function (file) {
			if (file.status === 'error') {
				// console.error('file has error status and can\'t be removed', file);
				return;
			}

			const id = file.previewElement?.dataset?.id;
			updateHiddenInput({
				action: 'remove',
				payload: { id },
			});


			try {
				const token = await request_captcha('file_remove');

				const response = await fetch('/wp-json/contract-form/attachment-delete', {
					method: 'POST',
					body: JSON.stringify({
						token: token,
						'contract-id': getContractID(),
						filename: id
					}),
					headers: {
						'Content-Type': 'application/json',
					}
				}).then((r) => r.json());


				if (!response.success) {
					console.error('file removal response was not successfull', response)
				}
			} catch (err) {
				console.error('file removal catch', err)
			}

		},
		error: function (file, response) {
			let message = 'Przesyłanie pliku nie powiodło się. Spróbuj ponownie później';

			if (response && typeof response === 'string') {
				message = response;
				console.error('dropzone error', response);
			} else if (response?.message) {
				console.error('dropzone error', response.message);
			}

			file.previewElement.querySelector('[data-dz-errormessage]').textContent = message;
		},
		/**
		 *
		 * @param file
		 * @param {{
		 *     success: boolean;
		 *     filename: string;
		 *     not_prefixed_filename: string;
		 *     size: number;
		 * }} response
		 */
		success: function (file, response) {
			const dropzoneInstance = this;
			if (response.success) {
				const { success, ...rest } = response;
				wrapPreviewElement(file.previewElement, {
					id: response.filename,
					title: response.not_prefixed_filename
				});
				updateHiddenInput({
					action: 'add', payload: { id: response.filename, ...rest },
				});
			} else {
				console.error('WRONG FILE', response);
				dropzoneInstance.removeFile(file);
			}
		},
		sending: async function (file, xhr, formData) {
			submitElement.disabled = true;
			formElement.classList.add('submitting');

			formData.append('contract-id', getContractID());
			formData.append('token', recaptcha_token);
		},
		complete: function () {
			submitElement.disabled = false;
			formElement.classList.remove('submitting');
		},
		addedfiles: async function () {
			const dropzoneInstance = this;
			const acceptedFiles = dropzoneInstance.getAcceptedFiles().filter(file => file.status === 'added');
			for (const acceptedFile of acceptedFiles) {
				try {
					recaptcha_token = await request_captcha('file_upload');
					dropzoneInstance.processFile(acceptedFile);
					await sleep(2000);
				} catch (err) {
					console.error('captcha or processing error', err)
				}
			}
		}
	}
	baseConfig.init = function () {
		const dropzoneInstance = this;
		for (const [eventName, eventFn] of Object.entries(events)) {
			dropzoneInstance.on(eventName, eventFn);
		}

		const fieldName = hiddenInputElement.name;
		const persistedData = getPersistedForm(formId)?.form?.data?.[fieldName] || [];

		try {
			const parsedData = JSON.parse(persistedData);
			for (const entry of parsedData) {
				const mockFile = {
					id: entry.id,
					name: entry.not_prefixed_filename,
					size: entry.size,
					persisted: true,
					accepted: true,
				};
				dropzoneInstance.files.push(mockFile);
				dropzoneInstance.displayExistingFile(mockFile, '');
			}

			if (dropzoneInstance.options.maxFiles <= parsedData.length) {
				dropzoneInstance.element.classList.add("dz-max-files-reached");
			}

		} catch (error) {
		}
	};

	const translations = {
		dictDefaultMessage: "Upuść pliki tutaj",
		dictFallbackMessage: "Twoja przeglądarka nie wspiera przesyłania plików za pomocą \"drag'n'drop\"",
		// dictFallbackText: "Please use the fallback form below to upload your files like in the olden days.",
		dictFileTooBig: "Plik jest za duży ({{filesize}}MB). Maksymalny rozmiar to: {{maxFilesize}}MB.",
		dictInvalidFileType: 'Niedozwolony typ pliku',
		dictResponseError: "Na serwerze wystąpił błąd (Kod: {{statusCode}}).",
		dictCancelUpload: "Anuluj wysyłanie",
		dictCancelUploadConfirmation: "Czy na pewno chcesz anulować wysyłanie tego pliku?",
		dictRemoveFile: "Usuń plik",
		dictMaxFilesExceeded: 'Nie możesz przesłać więcej plików',
	};

	window.dropzones.push(new Dropzone(dropzoneAreaElement, {
		...baseConfig,
		...translations,
	}));
}
